import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Form,
  Image,
  Input,
  message,
  Select,
  Space,
  Spin,
  Upload
} from "antd";
import type { RcFile } from "antd/es/upload";
import {
  AsYouType,
  CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberWithError
} from "libphonenumber-js";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ContentSkeleton from "../../../components/common/ContentSkeleton";
import { PERMISSIONS } from "../../../constants/permissions";
import { useFileUpload } from "../../../hooks/useFileUpload";
import { deviceApi } from "../../../services/api";
import { storageApi } from "../../../services/storageApi";
import type { DeviceRegistrationFormData } from "../../../types/device.types";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { StorageMetadata } from "../../../types/storage.types";
import { validatePhoneNumber } from "../../../utils/validation";

const { Option } = Select;

const RegisterDevicePage: React.FC = () => {
  const [form] = Form.useForm<DeviceRegistrationFormData>();
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>("BD");
  const [nidFrontPreview, setNidFrontPreview] = useState<string | null>(null);
  const [nidBackPreview, setNidBackPreview] = useState<string | null>(null);
  const [customerPhotoPreview, setCustomerPhotoPreview] = useState<string | null>(null);
  const [nidFrontFile, setNidFrontFile] = useState<RcFile | null>(null);
  const [nidBackFile, setNidBackFile] = useState<RcFile | null>(null);
  const [customerPhotoFile, setCustomerPhotoFile] = useState<RcFile | null>(null);
  const {user} = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [deviceId, setDeviceId] = useState<number | null>(null);

  const countries = getCountries();

  // Fetch device data when in edit mode
  useEffect(() => {
    if (location.state?.id) {
      setIsEditMode(true);
      setDeviceId(location.state.id);
      fetchDeviceData(location.state.id);
    }
  }, [location.state]);

  /**
   * Turns a stored object key into a displayable pre-signed URL.
   *
   * A failure here must not fail the whole edit form - a missing or expired
   * object should leave that one preview empty and let the rest of the device
   * load, so the error is logged rather than surfaced as a message.
   */
  const resolvePreview = async (
    key: string | null | undefined,
    setPreview: (url: string | null) => void
  ) => {
    if (!key) return;
    try {
      setPreview(await storageApi.getDownloadUrl(key));
    } catch (error) {
      console.error(`Failed to resolve download URL for ${key}`, error);
    }
  };

  const fetchDeviceData = async (id: number) => {
    try {
      setLoading(true);
      const response = await deviceApi.getDevice(id);
      if (response.status === "SUCCESS" && response.data) {
        const device = response.data;
        
        // Determine country code from phone number
        let countryCode = "BD";
        if (device.customerPhone) {
          try {
            const phoneNumber = parsePhoneNumberWithError(device.customerPhone);
            countryCode = phoneNumber.country as CountryCode;
            setSelectedCountry(countryCode as CountryCode);
          } catch (error) {
            setSelectedCountry("BD");
          }
        }

        // Set form values
        form.setFieldsValue({
          deviceModel: device.deviceModel,
          customerName: device.customerName,
          customerPhone: device.customerPhone,
          customerAddress: device.customerAddress,
          imei1: device.imei1,
          imei2: device.imei2,
          price: device.price,
          downPayment: device.downPayment,
          monthlyEmiAmount: device.monthlyEmiAmount,
          emiPeriod: device.emiPeriod,
          countryCode: countryCode,
        });

        // The API returns the R2 object key, not a URL - values look like
        // "<sellerId>/<deviceId>/customer_photo.jpg". Feeding that straight to
        // <Image src> made the browser resolve it against the dashboard origin
        // (lpanel.../<key> -> 404), so all three images were broken whenever an
        // existing device was reopened. Exchange each key for a short-lived
        // pre-signed download URL, which is also why the bucket can stay
        // private - these are customer NID photos.
        await Promise.all([
          resolvePreview(device.customerNidFront, setNidFrontPreview),
          resolvePreview(device.customerNidBack, setNidBackPreview),
          resolvePreview(device.customerPhoto, setCustomerPhotoPreview),
        ]);
      }
    } catch (error) {
      message.error("Failed to fetch device data");
    } finally {
      setLoading(false);
    }
  };

  const { uploadFile: uploadNidFront, uploading: uploadingNidFront } = useFileUpload({
    generateKey: (file, metadata) => `${metadata?.sellerId}/${metadata?.deviceId}/customer_nid_front.${file.name.split('.').pop()}`,
    onError: (error) => message.error(`Failed to upload NID front: ${error.message}`)
  });

  const { uploadFile: uploadNidBack, uploading: uploadingNidBack } = useFileUpload({
    generateKey: (file, metadata) => `${metadata?.sellerId}/${metadata?.deviceId}/customer_nid_back.${file.name.split('.').pop()}`,
    onError: (error) => message.error(`Failed to upload NID back: ${error.message}`)
  });

  const { uploadFile: uploadCustomerPhoto, uploading: uploadingCustomerPhoto } = useFileUpload({
    generateKey: (file, metadata) => `${metadata?.sellerId}/${metadata?.deviceId}/customer_photo.${file.name.split('.').pop()}`,
    onError: (error) => message.error(`Failed to upload customer photo: ${error.message}`)
  });

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatter = new AsYouType(selectedCountry);
    const formattedNumber = formatter.input(e.target.value);
    form.setFieldsValue({ customerPhone: formattedNumber });
  };

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value as CountryCode);
    const phoneValue = form.getFieldValue("customerPhone");
    if (phoneValue) {
      try {
        const phoneNumber = parsePhoneNumberWithError(
          phoneValue,
          selectedCountry
        );
        if (phoneNumber.isValid()) {
          form.setFieldsValue({ customerPhone: phoneNumber.format("NATIONAL") });
        }
      } catch (error) {
        // Clear the phone field if parsing fails
        form.setFieldsValue({ customerPhone: "" });
      }
    }
  };

  const beforeUpload = (file: RcFile, type: 'customerNidFront' | 'customerNidBack' | 'customerPhoto') => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPG/PNG files!");
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error("Image must be smaller than 2MB!");
      return false;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = () => {
      switch (type) {
        case 'customerNidFront':
          setNidFrontPreview(reader.result as string);
          setNidFrontFile(file);
          break;
        case 'customerNidBack':
          setNidBackPreview(reader.result as string);
          setNidBackFile(file);
          break;
        case 'customerPhoto':
          setCustomerPhotoPreview(reader.result as string);
          setCustomerPhotoFile(file);
          break;
      }
    };
    reader.readAsDataURL(file);

    form.setFieldValue(type, file.name);
    
    return false;
  };

  const onFinish = async (values: DeviceRegistrationFormData) => {
    try {
      setLoading(true);

      // Format phone number to E.164 format
      const phoneNumber = parsePhoneNumberWithError(
        values.customerPhone,
        selectedCountry
      );
      const formattedPhone = phoneNumber.format("E.164");

      const formData = {
        ...values,
        customerPhone: formattedPhone,
        firstEmiDate: values.firstEmiDate?.format("DD-MM-YYYY"),
      };

      let response;
      let resultDeviceId;

      if (isEditMode && deviceId) {
        response = await deviceApi.updateDevice(deviceId, formData);
        resultDeviceId = deviceId;
      } else {
        response = await deviceApi.registerDevice(formData);
        resultDeviceId = response.data?.id;
      }

      if (response.status === "SUCCESS") {
        const sellerId = user?.sellerId || user?.username || '';
        const uploadMetadata: StorageMetadata = { sellerId, deviceId: resultDeviceId, fileName: '' };
        
        const uploadPromises = [];
        
        if (nidFrontFile) {
          uploadMetadata.fileName = `customer_nid_front.${nidFrontFile.name.split('.').pop()}`;
          uploadPromises.push(uploadNidFront(nidFrontFile, uploadMetadata));
        }
        
        if (nidBackFile) {
          uploadMetadata.fileName = `customer_nid_back.${nidBackFile.name.split('.').pop()}`;
          uploadPromises.push(uploadNidBack(nidBackFile, uploadMetadata));
        }
        
        if (customerPhotoFile) {
          uploadMetadata.fileName = `customer_photo.${customerPhotoFile.name.split('.').pop()}`;
          uploadPromises.push(uploadCustomerPhoto(customerPhotoFile, uploadMetadata));
        }
        
        await Promise.all(uploadPromises);
        
        message.success(`Device ${isEditMode ? 'updated' : 'registered'} successfully`);
        form.resetFields();
        setNidFrontPreview(null);
        setNidBackPreview(null);
        setCustomerPhotoPreview(null);
        setNidFrontFile(null);
        setNidBackFile(null);
        setCustomerPhotoFile(null);
        navigate(PERMISSIONS.LIST_DEVICES.route);
      } else {
        message.error(response.message || `Failed to ${isEditMode ? 'update' : 'register'} device`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `Failed to ${isEditMode ? 'update' : 'register'} device`;
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setNidFrontPreview(null);
    setNidBackPreview(null);
    setCustomerPhotoPreview(null);
    setNidFrontFile(null);
    setNidBackFile(null);
    setCustomerPhotoFile(null);
  };

  if (loading) {
    return <ContentSkeleton cardCount={1} rows={8} />;
  }

  return (
    <Card
      title={isEditMode ? "Edit Device" : "Register Device"}
      style={{ maxWidth: 800, margin: "0 auto" }}
      extra={
        <Button onClick={() => navigate(PERMISSIONS.LIST_DEVICES.route)}>
          Back to List
        </Button>
      }
    >
      <Spin
        spinning={loading || uploadingNidFront || uploadingNidBack || uploadingCustomerPhoto}
        tip="Processing..."
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            countryCode: selectedCountry
          }}
        >
          <Form.Item
            name="deviceModel"
            label="Device Model"
            rules={[{ required: true, message: "Please enter device model" }]}
          >
            <Input placeholder="Enter device model" />
          </Form.Item>

          <Form.Item
            name="customerName"
            label="Customer Name"
            rules={[{ required: true, message: "Please enter customer name" }]}
          >
            <Input placeholder="Enter customer name" />
          </Form.Item>

          <Form.Item label="Customer Phone" required style={{ marginBottom: 0 }}>
            <Space.Compact style={{ width: "100%" }}>
              <Form.Item
                name="countryCode"
                initialValue={selectedCountry || "BD"}
                style={{ width: "30%" }}
              >
                <Select onChange={handleCountryChange}>
                  {countries.map((country: CountryCode) => (
                    <Option key={country} value={country}>
                      {country} (+{getCountryCallingCode(country)})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="customerPhone"
                style={{ width: "70%" }}
                rules={[
                  {
                    validator: (_, value) =>
                      validatePhoneNumber(_, value, selectedCountry),
                  },
                ]}
              >
                <Input
                  onChange={handlePhoneChange}
                  placeholder="Enter phone number"
                />
              </Form.Item>
            </Space.Compact>
          </Form.Item>

          <Form.Item
            name="customerAddress"
            label="Customer Address"
            rules={[{ required: true, message: "Please enter customer address" }]}
          >
            <Input.TextArea placeholder="Enter customer address" />
          </Form.Item>

          <Form.Item
            name="imei1"
            label="IMEI 1"
            rules={[{ required: true, message: "Please enter IMEI 1" }]}
          >
            <Input placeholder="Enter IMEI 1" />
          </Form.Item>

          <Form.Item name="imei2" label="IMEI 2">
            <Input placeholder="Enter IMEI 2" />
          </Form.Item>

          <Form.Item
            name="customerNidFront"
            label="Customer NID Front"
            valuePropName="customerNidFront"
            getValueFromEvent={(e) => e?.fileList?.[0]?.response?.key}
          >
            <Upload
              name="customerNidFront"
              listType="picture-card"
              showUploadList={false}
              beforeUpload={(file) => beforeUpload(file, 'customerNidFront')}
            >
              {nidFrontPreview ? (
                <Image src={nidFrontPreview} alt="NID front" style={{ width: '100%' }} />
              ) : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Upload NID Front</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item
            name="customerNidBack"
            label="Customer NID Back"
            valuePropName="customerNidBack"
            getValueFromEvent={(e) => e?.fileList?.[0]?.response?.key}
          >
            <Upload
              name="customerNidBack"
              listType="picture-card"
              showUploadList={false}
              beforeUpload={(file) => beforeUpload(file, 'customerNidBack')}
            >
              {nidBackPreview ? (
                <Image src={nidBackPreview} alt="NID back" style={{ width: '100%' }} />
              ) : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Upload NID Back</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item
            name="customerPhoto"
            label="Customer Photo"
            valuePropName="customerPhoto"
            getValueFromEvent={(e) => e?.fileList?.[0]?.response?.key}
          >
            <Upload
              name="customerPhoto"
              listType="picture-card"
              showUploadList={false}
              beforeUpload={(file) => beforeUpload(file, 'customerPhoto')}
            >
              {customerPhotoPreview ? (
                <Image src={customerPhotoPreview} alt="Customer" style={{ width: '100%' }} />
              ) : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Upload Photo</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {isEditMode ? "Update Device" : "Register Device"}
              </Button>
              <Button onClick={handleReset}>Reset</Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </Card>
  );
};

export default RegisterDevicePage;
